import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import type { ChangeEvent } from 'react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
	UsersIcon,
	ClockIcon,
	MagnifyingGlassIcon,
	PlusIcon,
	PencilSquareIcon,
	EyeIcon,
	EnvelopeIcon,
	PhoneIcon,
	UserIcon,
	ExclamationTriangleIcon,
	ChevronDownIcon,
	CameraIcon,
	XMarkIcon,
	PhotoIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import AppLayout from '../../components/layout/AppLayout';
import { Button, Badge, Modal, Drawer, Spinner } from '../../components/ui';
import useProviderProfileStore from '../../stores/providerProfileStore';
import staffApi from '../../api/staff';
import { appointmentsApi, rolesApi, ApiError } from '../../api';
import useStaffInfoRequestStatuses from '../../hooks/useStaffInfoRequestStatuses';
import type { InfoRequestEntry } from '../../hooks/useStaffInfoRequestStatuses';
import type { StaffMember, StaffFormData } from '../../types/staff';
import type { Role } from '../../types/roles';
import type { ServiceTask } from '../../types/appointments';
import { getUniqueTasks, formatTaskType } from '../../utils/taskHelpers';
import { readImageAsResizedDataUrl } from '../../utils/imageResize';
import { RotationStaffFinder, EngagementsTravelModal } from '../../components/providers';
import { MapPinIcon } from '@heroicons/react/24/outline';

// ---- Constants ----
const AVATAR_COLORS = [
	'#667eea',
	'#764ba2',
	'#f59e0b',
	'#10b981',
	'#ec4899',
	'#3b82f6',
	'#8b5cf6',
	'#f97316',
	'#06b6d4',
	'#14b8a6',
	'#a855f7',
	'#84cc16',
];

const getAvatarColor = (id: number) => AVATAR_COLORS[id % AVATAR_COLORS.length];

const emptyFormData: StaffFormData = {
	firstName: '',
	lastName: '',
	email: '',
	phone: '',
	roleId: null,
	status: 'ACTIVE',
	staffType: 'PERMANENT',
	staffGrade: 'ENTRY',
	hireDate: new Date().toISOString().split('T')[0],
	commissionRate: 0,
	selectedTaskIds: [],
	locationIds: [],
};

const staffMemberToFormData = (s: StaffMember): StaffFormData => ({
	firstName: s.firstName,
	lastName: s.lastName,
	email: s.email,
	phone: s.phone,
	roleId: s.roleId ?? null,
	status: s.status,
	staffType: s.staffType,
	staffGrade: s.staffGrade,
	hireDate: s.hireDate,
	commissionRate: s.commissionRate,
	selectedTaskIds: s.selectedTaskIds ?? [],
	locationIds: s.locationIds ?? [],
});

// ---- Helpers ----
const getInitials = (first: string, last: string) =>
	`${first.charAt(0)}${last.charAt(0)}`.toUpperCase();

const getStatusBadge = (staff: StaffMember) => {
	switch (staff.status) {
		case 'ACTIVE':
			return <Badge variant="success">{staff.statusName}</Badge>;
		case 'ON_LEAVE':
			return <Badge variant="warning">{staff.statusName}</Badge>;
		case 'INACTIVE':
			return <Badge variant="danger">{staff.statusName}</Badge>;
		case 'TERMINATED':
			return <Badge variant="danger">{staff.statusName}</Badge>;
		default:
			return <Badge variant="info">{staff.statusName}</Badge>;
	}
};

const getRoleBadge = (staff: StaffMember) => {
	switch (staff.role) {
		case 'BRAIDER':
			return <Badge variant="primary">{staff.roleName}</Badge>;
		case 'STYLIST':
			return <Badge variant="info">{staff.roleName}</Badge>;
		case 'MANAGER':
			return <Badge variant="warning">{staff.roleName}</Badge>;
		case 'RECEPTIONIST':
			return <Badge variant="danger">{staff.roleName}</Badge>;
		default:
			return <Badge variant="info">{staff.roleName}</Badge>;
	}
};

const formatStamp = (iso: string | null | undefined) => {
	if (!iso) return null;
	const parsed = new Date(iso);
	return Number.isNaN(parsed.getTime()) ? null : format(parsed, 'MMM d, yyyy');
};

// ---- Sub-components ----

interface InfoRequestCellProps {
	/** undefined = not loaded yet · null = status unknown (fetch failed) */
	info: InfoRequestEntry | undefined;
	isSending: boolean;
	onSend: () => void;
}

/**
 * Status and action merged into one cell: the cell always answers both "what
 * state is this in?" and "what can I do about it?". A Request button next to a
 * COMPLETED badge would invite an action that shouldn't happen, so the button
 * only exists in the states where sending is valid (NOT_SENT / EXPIRED). That
 * also satisfies AC5 by construction — there's no pending request to duplicate.
 *
 * Both unloaded and unknown withhold the button. Keying the skeleton off `info`
 * rather than an isLoading flag matters: the flag is only set inside an effect,
 * so it's still false on the frame the staff list first paints, which would
 * briefly offer a live Request button on every row.
 */
const InfoRequestCell = ({ info, isSending, onSend }: InfoRequestCellProps) => {
	if (info === undefined) {
		return <span className="info-request__skeleton" aria-label="Loading status" />;
	}

	if (info === null) {
		return (
			<span
				className="info-request info-request--unknown"
				title="Couldn't load this status. Refresh to try again."
			>
				Unavailable
			</span>
		);
	}

	const status = info.status;

	if (status === 'PENDING') {
		const sent = formatStamp(info.requestedAt);
		const expires = formatStamp(info.expiresAt);
		const tip = [sent && `Sent ${sent}`, expires && `Expires ${expires}`]
			.filter(Boolean)
			.join(' · ');
		return (
			<span className="info-request info-request--pending" title={tip || 'Request pending'}>
				<span className="info-request__dot" />
				Pending
			</span>
		);
	}

	if (status === 'COMPLETED') {
		const completed = formatStamp(info.completedAt);
		return (
			<span
				className="info-request info-request--completed"
				title={completed ? `Submitted ${completed}` : 'Submitted'}
			>
				<CheckCircleIcon className="w-3.5 h-3.5" />
				Submitted
			</span>
		);
	}

	if (status === 'EXPIRED') {
		const expired = formatStamp(info.expiresAt);
		return (
			<div className="flex items-center gap-2">
				<span
					className="info-request info-request--expired"
					title={expired ? `Expired ${expired}` : 'Request expired'}
				>
					<span className="info-request__dot" />
					Expired
				</span>
				<Button
					size="sm"
					variant="secondary"
					isLoading={isSending}
					onClick={e => {
						e.stopPropagation();
						onSend();
					}}
				>
					Resend
				</Button>
			</div>
		);
	}

	return (
		<Button
			size="sm"
			variant="secondary"
			isLoading={isSending}
			onClick={e => {
				e.stopPropagation();
				onSend();
			}}
		>
			Request
		</Button>
	);
};

// Stats Grid
/**
 * "Works At": this salon only, or the number of salons the person works at. The
 * latter opens the Engagements & Travel view so the 30-minute travel rule between
 * salons can be read off their day.
 */
const WorksAtCell = ({ staff, onOpen }: { staff: StaffMember; onOpen: () => void }) => {
	const count = staff.salonCount ?? 1;
	if (count <= 1) {
		return (
			<span className="text-sm text-gray-500 dark:text-gray-400" title="Only works here">
				This salon only
			</span>
		);
	}
	return (
		<button
			type="button"
			className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-900/50 cursor-pointer"
			title="Click to see engagements and travel gaps"
			onClick={e => {
				e.stopPropagation();
				onOpen();
			}}
		>
			<MapPinIcon className="w-3.5 h-3.5" /> {count} salons
		</button>
	);
};

const StatsGrid = ({ staff }: { staff: StaffMember[] }) => {
	const totalStaff = staff.length;
	const activeStaff = staff.filter(s => s.status === 'ACTIVE').length;
	const stylistsBraiders = staff.filter(s => s.role === 'STYLIST' || s.role === 'BRAIDER').length;
	const onLeave = staff.filter(s => s.status === 'ON_LEAVE').length;

	const stats = [
		{
			label: 'Total Staff',
			value: totalStaff,
			icon: UsersIcon,
			colorClass: 'staff-stat-card--purple',
		},
		{
			label: 'Active',
			value: activeStaff,
			icon: CheckCircleIcon,
			colorClass: 'staff-stat-card--green',
		},
		{
			label: 'Stylists / Braiders',
			value: stylistsBraiders,
			icon: UserIcon,
			colorClass: 'staff-stat-card--blue',
		},
		{
			label: 'On Leave',
			value: onLeave,
			icon: ClockIcon,
			colorClass: 'staff-stat-card--orange',
		},
	];

	return (
		<div className="staff-stats">
			{stats.map(stat => (
				<div key={stat.label} className={`staff-stat-card ${stat.colorClass}`}>
					<div className="staff-stat-card__icon">
						<stat.icon className="w-6 h-6" />
					</div>
					<div className="staff-stat-card__content">
						<span className="staff-stat-card__value">{stat.value}</span>
						<span className="staff-stat-card__label">{stat.label}</span>
					</div>
				</div>
			))}
		</div>
	);
};

// Staff Detail Drawer
interface StaffDetailDrawerProps {
	staff: StaffMember | null;
	isOpen: boolean;
	onClose: () => void;
	onEdit: (staff: StaffMember) => void;
}

const StaffDetailDrawer = ({ staff, isOpen, onClose, onEdit }: StaffDetailDrawerProps) => {
	return (
		<Drawer
			isOpen={isOpen}
			onClose={onClose}
			title="Staff Details"
			width="md"
			footer={
				staff && (
					<div className="flex gap-3">
						<Button
							variant="primary"
							className="flex-1"
							onClick={() => {
								onClose();
								onEdit(staff);
							}}
						>
							<PencilSquareIcon className="w-4 h-4" />
							Edit Staff
						</Button>
						<Button variant="secondary" className="flex-1" onClick={onClose}>
							Close
						</Button>
					</div>
				)
			}
		>
			{staff && (
				<div className="staff-drawer">
					{/* Avatar + Name */}
					<div className="staff-drawer__header">
						<div
							className="staff-drawer__avatar"
							style={{ background: getAvatarColor(staff.id) }}
						>
							{getInitials(staff.firstName, staff.lastName)}
						</div>
						<div className="staff-drawer__name-block">
							<h3 className="staff-drawer__name">
								{staff.firstName} {staff.lastName}
							</h3>
							<div className="flex items-center gap-2 mt-1">
								{getRoleBadge(staff)}
								{getStatusBadge(staff)}
							</div>
						</div>
					</div>

					{/* Contact Info */}
					<div className="staff-drawer__section">
						<h4 className="staff-drawer__section-title">Contact Information</h4>
						<div className="staff-drawer__detail-row">
							<EnvelopeIcon className="w-4 h-4 text-gray-400 shrink-0" />
							<span>{staff.email}</span>
						</div>
						<div className="staff-drawer__detail-row">
							<PhoneIcon className="w-4 h-4 text-gray-400 shrink-0" />
							<span>{staff.phone}</span>
						</div>
					</div>

					{/* Employment Details */}
					<div className="staff-drawer__section">
						<h4 className="staff-drawer__section-title">Employment Details</h4>
						<div className="staff-drawer__grid">
							<div className="staff-drawer__field">
								<span className="staff-drawer__field-label">Staff Type</span>
								<span className="staff-drawer__field-value">
									{staff.staffTypeName}
								</span>
							</div>
							<div className="staff-drawer__field">
								<span className="staff-drawer__field-label">Hire Date</span>
								<span className="staff-drawer__field-value">
									{new Date(staff.hireDate).toLocaleDateString('en-US', {
										month: 'short',
										day: 'numeric',
										year: 'numeric',
									})}
								</span>
							</div>
							<div className="staff-drawer__field">
								<span className="staff-drawer__field-label">Grade</span>
								<span className="staff-drawer__field-value">
									{staff.staffGradeName}
								</span>
							</div>
							<div className="staff-drawer__field">
								<span className="staff-drawer__field-label">Commission Rate</span>
								<span className="staff-drawer__field-value">
									{staff.commissionRate}%
								</span>
							</div>
						</div>
					</div>

					{/* Work Portfolio */}
					<div className="staff-drawer__section">
						<h4 className="staff-drawer__section-title">Work Portfolio</h4>
						{staff.portfolioImageUrls && staff.portfolioImageUrls.length > 0 ? (
							<div className="flex flex-wrap gap-2">
								{staff.portfolioImageUrls.map((url, idx) => (
									<div
										key={url}
										className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700"
									>
										<img
											src={url}
											alt={`Work photo ${idx + 1}`}
											loading="lazy"
											className="w-full h-full object-cover"
										/>
									</div>
								))}
							</div>
						) : (
							<p className="text-xs text-gray-500 dark:text-gray-400">
								No photos yet. Add some from Edit Staff to show this stylist&apos;s
								work on the booking page.
							</p>
						)}
					</div>
				</div>
			)}
		</Drawer>
	);
};

// Add/Edit Staff Modal
interface AddEditStaffModalProps {
	isOpen: boolean;
	onClose: () => void;
	staff: StaffMember | null;
	onSave: (data: StaffFormData, portfolio: string[], id?: number) => void;
	isSaving: boolean;
	allServiceTasks: ServiceTask[];
	isLoadingServiceTasks: boolean;
	roles: Role[];
	locationId: number | null;
	locations: {
		id: number;
		locationName: string | null;
		address: string;
		city: string;
		state: string;
		active: boolean;
	}[];
}

const AddEditStaffModal = ({
	isOpen,
	onClose,
	staff,
	onSave,
	isSaving,
	allServiceTasks,
	isLoadingServiceTasks,
	roles,
	locationId,
	locations,
}: AddEditStaffModalProps) => {
	const isEditing = !!staff;
	const [formData, setFormData] = useState<StaffFormData>(emptyFormData);
	const [isTasksExpanded, setIsTasksExpanded] = useState(false);
	const [selectedTaskIds, setSelectedTaskIds] = useState<Set<number>>(new Set());
	const [isLoadingDetail, setIsLoadingDetail] = useState(false);

	const [selectedLocationIds, setSelectedLocationIds] = useState<Set<number>>(new Set());
	// Work portfolio: existing image URLs and/or freshly chosen base64 data URLs.
	const [portfolio, setPortfolio] = useState<string[]>([]);
	const [isReadingPhotos, setIsReadingPhotos] = useState(false);
	const portfolioInputRef = useRef<HTMLInputElement>(null);

	// Reset form when modal opens with new staff
	useState(() => {
		if (staff) {
			setFormData(staffMemberToFormData(staff));
			setSelectedTaskIds(new Set(staff.selectedTaskIds ?? []));
			setSelectedLocationIds(new Set(staff.locationIds ?? []));
			setPortfolio(staff.portfolioImageUrls?.filter(Boolean) ?? []);
		} else {
			setFormData({ ...emptyFormData });
			setSelectedTaskIds(new Set());
			setPortfolio([]);
			// For new PERMANENT staff, default to active location; ON_CALL starts empty
			if (emptyFormData.staffType === 'PERMANENT' && locationId) {
				setSelectedLocationIds(new Set([locationId]));
			} else {
				setSelectedLocationIds(new Set());
			}
		}
		setIsTasksExpanded(false);
	});

	// Fetch staff detail to get current selectedTaskIds when editing
	useEffect(() => {
		if (isOpen && staff?.id && locationId) {
			setIsLoadingDetail(true);
			staffApi
				.getStaffDetail(staff.id, locationId)
				.then(detail => {
					setSelectedTaskIds(new Set(detail.selectedTaskIds ?? []));
					setSelectedLocationIds(new Set(detail.locationIds ?? []));
					setPortfolio(detail.portfolioImageUrls?.filter(Boolean) ?? []);
				})
				.catch(() => {
					// Fall back to what we have from list API
				})
				.finally(() => setIsLoadingDetail(false));
		}
	}, [isOpen, staff?.id, locationId]);

	const updateField = <K extends keyof StaffFormData>(key: K, value: StaffFormData[K]) => {
		setFormData(prev => ({ ...prev, [key]: value }));
	};

	// ─── Work portfolio ─────────────────────────────────────────
	const handlePortfolioFiles = async (e: ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files ?? []).filter(f => f.type.startsWith('image/'));
		if (portfolioInputRef.current) portfolioInputRef.current.value = '';
		if (files.length === 0) return;
		setIsReadingPhotos(true);
		try {
			const results = await Promise.allSettled(files.map(f => readImageAsResizedDataUrl(f)));
			const added = results
				.filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled')
				.map(r => r.value);
			if (added.length < files.length) {
				toast.error('Some photos could not be read and were skipped.');
			}
			if (added.length > 0) {
				setPortfolio(prev => [...prev, ...added]);
			}
		} finally {
			setIsReadingPhotos(false);
		}
	};

	const removePortfolioPhoto = (index: number) => {
		setPortfolio(prev => prev.filter((_, i) => i !== index));
	};

	/* Staff search handlers - hidden for now
	const handleSearchStaff = useCallback(async (query: string): Promise<StaffMember[]> => {
		return staffApi.searchStaff(query);
	}, []);

	const handleStaffSearchSelect = useCallback((selected: StaffMember) => {
		setFormData(staffMemberToFormData(selected));
		setSelectedTaskIds(new Set(selected.selectedTaskIds ?? []));
	}, []);

	const handleSearchClear = useCallback(() => {
		setFormData({ ...emptyFormData });
		setSelectedTaskIds(new Set());
	}, []);
	*/

	const toggleTask = useCallback((taskId: number) => {
		setSelectedTaskIds(prev => {
			const next = new Set(prev);
			if (next.has(taskId)) {
				next.delete(taskId);
			} else {
				next.add(taskId);
			}
			return next;
		});
	}, []);

	const toggleAllForService = useCallback((service: ServiceTask, select: boolean) => {
		const uniqueTasks = getUniqueTasks(service);
		setSelectedTaskIds(prev => {
			const next = new Set(prev);
			for (const t of uniqueTasks) {
				if (select) {
					next.add(t.serviceTaskId);
				} else {
					next.delete(t.serviceTaskId);
				}
			}
			return next;
		});
	}, []);

	const toggleAllServices = useCallback(
		(select: boolean) => {
			if (select) {
				const allIds = new Set<number>();
				for (const svc of allServiceTasks) {
					for (const t of getUniqueTasks(svc)) {
						allIds.add(t.serviceTaskId);
					}
				}
				setSelectedTaskIds(allIds);
			} else {
				setSelectedTaskIds(new Set());
			}
		},
		[allServiceTasks]
	);

	// Derive summary counts
	const taskSummary = useMemo(() => {
		let serviceCount = 0;
		let taskCount = selectedTaskIds.size;
		for (const svc of allServiceTasks) {
			const uniqueTasks = getUniqueTasks(svc);
			if (uniqueTasks.some(t => selectedTaskIds.has(t.serviceTaskId))) {
				serviceCount++;
			}
		}
		return { serviceCount, taskCount };
	}, [selectedTaskIds, allServiceTasks]);

	const handleSubmit = () => {
		if (
			!formData.firstName.trim() ||
			!formData.lastName.trim() ||
			!formData.email.trim() ||
			!formData.phone.trim()
		) {
			toast.error('Please fill in all required fields.');
			return;
		}
		if (formData.roleId == null) {
			toast.error('Please select a role.');
			return;
		}
		if (selectedLocationIds.size === 0) {
			toast.error('Please select at least one location.');
			return;
		}
		const finalData: StaffFormData = {
			...formData,
			selectedTaskIds: Array.from(selectedTaskIds),
			locationIds: Array.from(selectedLocationIds),
		};
		onSave(finalData, portfolio, staff?.id);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={isEditing ? 'Edit Staff Member' : 'Add Staff Member'}
			size="xl"
			bodyClassName="max-h-[70vh] overflow-y-auto"
		>
			<div className="space-y-5">
				{/* Staff Search - Add mode only (hidden for now) */}
				{/* {!isEditing && (
					<div className="pb-4 mb-1 border-b border-gray-200 dark:border-gray-700">
						<Autocomplete<StaffMember>
							label="Search Staff by Phone or Email"
							helperText="Search for an existing staff member to pre-fill the form"
							placeholder="Enter name, phone, or email to search..."
							onSearch={handleSearchStaff}
							onSelect={handleStaffSearchSelect}
							onClear={handleSearchClear}
							renderItem={s => (
								<div className="flex items-center gap-3">
									<div
										className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
										style={{ background: getAvatarColor(s.id) }}
									>
										{getInitials(s.firstName, s.lastName)}
									</div>
									<div className="flex-1 min-w-0">
										<div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
											{s.firstName} {s.lastName}
										</div>
										<div className="text-xs text-gray-500 dark:text-gray-400 truncate">
											{s.email} &middot; {s.phone}
										</div>
									</div>
								</div>
							)}
							getItemKey={s => s.id}
							minChars={3}
						/>
					</div>
				)} */}

				{/* Name Row */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							First Name <span className="provider-form__required">*</span>
						</label>
						<input
							type="text"
							className="provider-form__input"
							value={formData.firstName}
							onChange={e => updateField('firstName', e.target.value)}
							placeholder="First name"
						/>
					</div>
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							Last Name <span className="provider-form__required">*</span>
						</label>
						<input
							type="text"
							className="provider-form__input"
							value={formData.lastName}
							onChange={e => updateField('lastName', e.target.value)}
							placeholder="Last name"
						/>
					</div>
				</div>

				{/* Contact Row */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							Email <span className="provider-form__required">*</span>
						</label>
						<input
							type="email"
							className="provider-form__input"
							value={formData.email}
							onChange={e => updateField('email', e.target.value)}
							placeholder="email@example.com"
							readOnly={!!staff?.homeStaffId}
							title={
								staff?.homeStaffId
									? 'Managed by the salon that first hired them'
									: undefined
							}
						/>
						{staff?.homeStaffId && (
							<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
								Login email is managed by the salon that first hired them.
							</p>
						)}
					</div>
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							Phone <span className="provider-form__required">*</span>
						</label>
						<input
							type="tel"
							className="provider-form__input"
							value={formData.phone}
							onChange={e => updateField('phone', e.target.value)}
							placeholder="(555) 123-4567"
						/>
					</div>
				</div>

				{/* Role + Status */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">
							Role <span className="provider-form__required">*</span>
						</label>
						<select
							className="provider-form__input"
							value={formData.roleId ?? ''}
							onChange={e =>
								updateField('roleId', e.target.value ? Number(e.target.value) : null)
							}
						>
							<option value="">-- Select a Role --</option>
							{roles.map(r => (
								<option key={r.id} value={r.id}>
									{r.name}
								</option>
							))}
						</select>
					</div>
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Status</label>
						<select
							className="provider-form__input"
							value={formData.status}
							onChange={e => updateField('status', e.target.value)}
						>
							<option value="ACTIVE">Active</option>
							<option value="ON_LEAVE">On Leave</option>
							<option value="INACTIVE">Inactive</option>
							<option value="TERMINATED">Terminated</option>
						</select>
					</div>
				</div>

				{/* Staff Type + Hire Date */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Staff Type</label>
						<select
							className="provider-form__input"
							value={formData.staffType}
							onChange={e => {
								const newType = e.target.value;
								updateField('staffType', newType);
								if (newType === 'PERMANENT' && locationId) {
									setSelectedLocationIds(new Set([locationId]));
								}
							}}
						>
							<option value="PERMANENT">Permanent</option>
							<option value="ON_CALL">On Call</option>
						</select>
					</div>
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Hire Date</label>
						<input
							type="date"
							className="provider-form__input"
							value={formData.hireDate}
							onChange={e => updateField('hireDate', e.target.value)}
						/>
					</div>
				</div>

				{/* Location Assignment */}
				{locations.length > 0 && (
					<div>
						<label className="provider-form__label">
							Locations{' '}
							{formData.staffType === 'ON_CALL' && (
								<span className="text-red-500">*</span>
							)}
						</label>
						<p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
							{formData.staffType === 'PERMANENT'
								? 'Permanent staff are assigned to the current active location.'
								: 'Select one or more locations for this on-call staff member.'}
						</p>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
							{(formData.staffType === 'PERMANENT'
								? locations.filter(l => l.id === locationId)
								: locations
							).map(loc => {
								const isChecked = selectedLocationIds.has(loc.id);
								const isPermanent = formData.staffType === 'PERMANENT';
								return (
									<label
										key={loc.id}
										className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
											isChecked
												? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-400'
												: 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50'
										} ${isPermanent ? 'opacity-80 cursor-default' : 'hover:border-purple-300 dark:hover:border-purple-600'}`}
									>
										<input
											type="checkbox"
											checked={isChecked}
											disabled={isPermanent}
											onChange={() => {
												if (isPermanent) return;
												setSelectedLocationIds(prev => {
													const next = new Set(prev);
													if (next.has(loc.id)) {
														next.delete(loc.id);
													} else {
														next.add(loc.id);
													}
													return next;
												});
											}}
											className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
										/>
										<div className="flex-1 min-w-0">
											<p className="text-sm font-medium text-gray-900 dark:text-white truncate">
												{loc.locationName ||
													[loc.address, loc.city, loc.state]
														.filter(Boolean)
														.join(', ')}
											</p>
											<p className="text-xs text-gray-500 dark:text-gray-400">
												{loc.active ? 'Active' : 'Inactive'}
											</p>
										</div>
									</label>
								);
							})}
						</div>
					</div>
				)}

				{/* Grade + Commission */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Grade</label>
						<select
							className="provider-form__input"
							value={formData.staffGrade}
							onChange={e => updateField('staffGrade', e.target.value)}
						>
							<option value="ENTRY">Entry</option>
							<option value="EXPERT">Expert</option>
							<option value="PRO">Pro</option>
							<option value="MASTER">Master</option>
						</select>
					</div>
					<div className="provider-form__group mb-0">
						<label className="provider-form__label">Commission Rate (%)</label>
						<input
							type="number"
							className="provider-form__input"
							min={0}
							max={100}
							value={formData.commissionRate}
							onChange={e => updateField('commissionRate', Number(e.target.value))}
						/>
					</div>
				</div>

				{/* Services & Tasks Accordion */}
				<div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
					<button
						type="button"
						className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
						onClick={() => setIsTasksExpanded(!isTasksExpanded)}
					>
						<div className="flex items-center gap-2">
							<span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
								Services & Tasks
							</span>
							<span className="text-xs text-gray-500 dark:text-gray-400">
								{taskSummary.taskCount > 0
									? `${taskSummary.serviceCount} service${taskSummary.serviceCount !== 1 ? 's' : ''}, ${taskSummary.taskCount} task${taskSummary.taskCount !== 1 ? 's' : ''}`
									: 'No tasks assigned'}
							</span>
						</div>
						<ChevronDownIcon
							className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isTasksExpanded ? 'rotate-180' : ''}`}
						/>
					</button>

					{isTasksExpanded && (
						<div className="px-4 py-3 max-h-[280px] overflow-y-auto border-t border-gray-200 dark:border-gray-700">
							{isLoadingServiceTasks || isLoadingDetail ? (
								<div className="py-6 text-center">
									<Spinner size="sm" className="mx-auto mb-2 text-purple-600" />
									<p className="text-xs text-gray-500 dark:text-gray-400">
										Loading services & tasks...
									</p>
								</div>
							) : allServiceTasks.length === 0 ? (
								<p className="py-4 text-center text-sm text-gray-400 dark:text-gray-500">
									No services available for this location.
								</p>
							) : (
								<div className="space-y-3">
									{(() => {
										const allTaskIds: number[] = [];
										for (const svc of allServiceTasks) {
											for (const t of getUniqueTasks(svc)) {
												allTaskIds.push(t.serviceTaskId);
											}
										}
										const allSelectedGlobal =
											allTaskIds.length > 0 &&
											allTaskIds.every(id => selectedTaskIds.has(id));
										return (
											<div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
												<span className="text-xs font-medium text-gray-500 dark:text-gray-400">
													{selectedTaskIds.size} of {allTaskIds.length}{' '}
													tasks selected
												</span>
												<button
													type="button"
													className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
													onClick={() =>
														toggleAllServices(!allSelectedGlobal)
													}
												>
													{allSelectedGlobal
														? 'Deselect All'
														: 'Select All'}
												</button>
											</div>
										);
									})()}
									{allServiceTasks.map(service => {
										const uniqueTasks = getUniqueTasks(service);
										const allSelected = uniqueTasks.every(t =>
											selectedTaskIds.has(t.serviceTaskId)
										);
										const someSelected = uniqueTasks.some(t =>
											selectedTaskIds.has(t.serviceTaskId)
										);

										return (
											<div
												key={service.id}
												className="border border-gray-100 dark:border-gray-700 rounded-lg p-3"
											>
												<div className="flex items-center justify-between mb-2">
													<h5 className="text-sm font-medium text-gray-800 dark:text-gray-200">
														{service.serviceName}
													</h5>
													<button
														type="button"
														className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
														onClick={() =>
															toggleAllForService(
																service,
																!allSelected
															)
														}
													>
														{allSelected
															? 'Deselect All'
															: someSelected
																? 'Select All'
																: 'Select All'}
													</button>
												</div>
												<div className="flex flex-wrap gap-1.5">
													{uniqueTasks.map(task => {
														const isSelected = selectedTaskIds.has(
															task.serviceTaskId
														);
														return (
															<button
																key={task.serviceTaskId}
																type="button"
																className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer ${
																	isSelected
																		? 'bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white shadow-sm'
																		: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
																}`}
																onClick={() =>
																	toggleTask(task.serviceTaskId)
																}
															>
																{isSelected && (
																	<CheckCircleIcon className="w-3 h-3" />
																)}
																{formatTaskType(task.taskType)}
															</button>
														);
													})}
												</div>
											</div>
										);
									})}
								</div>
							)}
						</div>
					)}
				</div>

				{/* Work Portfolio */}
				<div>
					<label className="provider-form__label">Work Portfolio</label>
					<p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
						Shown to customers on the booking page&apos;s &ldquo;See Work&rdquo;
						gallery. The first photo doubles as the stylist&apos;s picture.
					</p>
					<div className="flex flex-wrap gap-3">
						{portfolio.map((src, idx) => (
							<div
								key={`${idx}-${src.slice(-24)}`}
								className="relative w-24 h-24 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 group"
							>
								<img
									src={src}
									alt={`Work photo ${idx + 1}`}
									className="w-full h-full object-cover"
								/>
								<button
									type="button"
									onClick={() => removePortfolioPhoto(idx)}
									title="Remove photo"
									aria-label={`Remove photo ${idx + 1}`}
									className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity cursor-pointer"
								>
									<XMarkIcon className="w-3 h-3" />
								</button>
							</div>
						))}
						<button
							type="button"
							onClick={() => portfolioInputRef.current?.click()}
							disabled={isReadingPhotos || isSaving}
							className="w-24 h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center justify-center gap-1 text-gray-400 hover:border-[#667eea] hover:text-[#667eea] transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
						>
							{isReadingPhotos ? (
								<Spinner size="sm" />
							) : (
								<>
									<CameraIcon className="w-6 h-6" />
									<span className="text-[10px] font-medium">Add Photos</span>
								</>
							)}
						</button>
						<input
							ref={portfolioInputRef}
							type="file"
							accept="image/*"
							multiple
							onChange={handlePortfolioFiles}
							className="hidden"
						/>
					</div>
					{portfolio.length === 0 && (
						<p className="flex items-center gap-1.5 mt-2 text-xs text-gray-400 dark:text-gray-500">
							<PhotoIcon className="w-4 h-4" />
							No photos yet.
						</p>
					)}
				</div>

				{/* Actions */}
				<div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
					<Button
						variant="secondary"
						className="flex-1"
						onClick={onClose}
						disabled={isSaving}
					>
						Cancel
					</Button>
					<Button
						variant="primary"
						className="flex-1"
						onClick={handleSubmit}
						disabled={isSaving}
					>
						{isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Staff Member'}
					</Button>
				</div>
			</div>
		</Modal>
	);
};

// ---- Main StaffManagement Page ----
const StaffManagement = () => {
	const { selectedLocationId, profile } = useProviderProfileStore();
	const [staffList, setStaffList] = useState<StaffMember[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [roleFilter, setRoleFilter] = useState('');
	const [statusFilter, setStatusFilter] = useState('');
	const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [allServiceTasks, setAllServiceTasks] = useState<ServiceTask[]>([]);
	const [isLoadingServiceTasks, setIsLoadingServiceTasks] = useState(false);
	const [roles, setRoles] = useState<Role[]>([]);
	const [tab, setTab] = useState<'team' | 'find'>('team');
	const [travelStaff, setTravelStaff] = useState<StaffMember | null>(null);

	// The header location, for the rotation search's "nearby first" default.
	const selectedLocation = useMemo(
		() => profile?.locations?.find(l => l.id === selectedLocationId) ?? null,
		[profile, selectedLocationId]
	);

	// Fetch service tasks for location
	const fetchServiceTasks = useCallback(async (locationId: number) => {
		setIsLoadingServiceTasks(true);
		try {
			const data = await appointmentsApi.getServiceTasks(locationId);
			setAllServiceTasks(data);
		} catch {
			setAllServiceTasks([]);
		} finally {
			setIsLoadingServiceTasks(false);
		}
	}, []);

	// Fetch staff list
	const fetchStaffList = useCallback(async (locationId: number) => {
		setIsLoading(true);
		setError(null);
		// Drop the previous location's staff before loading the next one. Without
		// this, staffIds still holds location A's ids while locationId is already
		// B, and the info-request fanout fires N mismatched staff/location pairs.
		// Invisible to the user: the table is gated behind the loading spinner.
		setStaffList([]);
		try {
			const data = await staffApi.listStaff(locationId);
			setStaffList(data);
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to load staff';
			setError(message);
			setStaffList([]);
		} finally {
			setIsLoading(false);
		}
	}, []);

	// Watch selectedLocationId and refetch
	useEffect(() => {
		if (selectedLocationId) {
			fetchStaffList(selectedLocationId);
			fetchServiceTasks(selectedLocationId);
		}
	}, [selectedLocationId, fetchStaffList, fetchServiceTasks]);

	// Fetch the provider's roles (provider-level, not location-specific) for the
	// Add/Edit dropdown and the list role filter.
	useEffect(() => {
		rolesApi
			.list()
			.then(setRoles)
			.catch(() => setRoles([]));
	}, []);

	// Filtered staff (local filtering)
	const filteredStaff = useMemo(() => {
		return staffList.filter(staff => {
			const matchesSearch =
				!searchQuery ||
				`${staff.firstName} ${staff.lastName}`
					.toLowerCase()
					.includes(searchQuery.toLowerCase()) ||
				staff.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
				staff.phone.includes(searchQuery);

			const matchesRole = !roleFilter || String(staff.roleId ?? '') === roleFilter;
			const matchesStatus = !statusFilter || staff.status === statusFilter;

			return matchesSearch && matchesRole && matchesStatus;
		});
	}, [staffList, searchQuery, roleFilter, statusFilter]);

	// Personal-info request status, one entry per staff member.
	// Keyed off the full list rather than filteredStaff: filtering is client-side,
	// so deriving from it would refire the whole fanout on every keystroke.
	const staffIds = useMemo(() => staffList.map(s => s.id), [staffList]);
	const { statuses: infoRequests, applyStatus: applyInfoRequest } = useStaffInfoRequestStatuses(
		staffIds,
		selectedLocationId ?? null
	);
	const [sendingInfoRequestId, setSendingInfoRequestId] = useState<number | null>(null);

	const handleSendInfoRequest = async (staff: StaffMember) => {
		if (!selectedLocationId) return;
		setSendingInfoRequestId(staff.id);
		try {
			const result = await staffApi.sendInfoRequest(staff.id, selectedLocationId);
			applyInfoRequest(staff.id, result);
			toast.success(`Information request sent to ${staff.firstName} ${staff.lastName}.`);
		} catch (err) {
			// The cell hides the button while a request is pending, so a 409 only
			// happens if this table is stale. Re-sync that row rather than just
			// reporting the error.
			if (err instanceof ApiError && err.status === 409) {
				toast.error('A request is already pending for this staff member.');
				try {
					applyInfoRequest(
						staff.id,
						await staffApi.getInfoRequest(staff.id, selectedLocationId)
					);
				} catch {
					// Leave the row as-is; the next load will reconcile it.
				}
			} else {
				const message = err instanceof Error ? err.message : 'Failed to send request';
				toast.error(message);
			}
		} finally {
			setSendingInfoRequestId(null);
		}
	};

	const handleViewStaff = (staff: StaffMember) => {
		setSelectedStaff(staff);
		setIsDrawerOpen(true);
	};

	const handleEditStaff = (staff: StaffMember) => {
		setEditingStaff(staff);
		setIsModalOpen(true);
	};

	const handleAddStaff = () => {
		setEditingStaff(null);
		setIsModalOpen(true);
	};

	const handleSaveStaff = async (data: StaffFormData, portfolio: string[], id?: number) => {
		if (!selectedLocationId) return;
		setIsSaving(true);
		try {
			let savedId = id;
			if (id) {
				await staffApi.updateStaff(id, selectedLocationId, data);
				toast.success('Staff member updated successfully!');
			} else {
				const created = await staffApi.createStaff(selectedLocationId, data);
				savedId = created.id;
				toast.success('Staff member added successfully!');
			}
			// The portfolio is persisted separately against the saved staff id, as the
			// legacy portal does. A photo failure never undoes the staff save itself.
			if (savedId) {
				try {
					await staffApi.updatePortfolio(savedId, selectedLocationId, portfolio);
				} catch (err) {
					const message =
						err instanceof Error ? err.message : 'Failed to save work photos';
					toast.error(`Staff saved, but the work photos were not: ${message}`);
				}
			}
			setIsModalOpen(false);
			setEditingStaff(null);
			fetchStaffList(selectedLocationId);
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to save staff member';
			toast.error(message);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
				<div>
					<div className="flex items-center gap-3 mb-1">
						<UsersIcon className="w-6 h-6 text-gray-400" />
						<h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
							Staff Management
						</h1>
					</div>
					<p className="text-sm text-gray-500 dark:text-gray-400">
						Manage your team members, roles, and assignments
					</p>
				</div>
				<Button variant="primary" onClick={handleAddStaff}>
					<PlusIcon className="w-5 h-5" />
					Add Staff Member
				</Button>
			</div>

			{/* Tabs */}
			<div className="flex gap-2 mb-5">
				{(
					[
						['team', `My Team${staffList.length ? ` (${staffList.length})` : ''}`],
						['find', 'Find Rotation Staff'],
					] as ['team' | 'find', string][]
				).map(([key, label]) => (
					<button
						key={key}
						type="button"
						onClick={() => setTab(key)}
						className={`px-4 py-2 rounded-full text-sm font-semibold border cursor-pointer transition-colors ${
							tab === key
								? 'bg-indigo-50 dark:bg-indigo-900/30 border-[#667eea] text-indigo-700 dark:text-indigo-300'
								: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-indigo-300'
						}`}
					>
						{label}
					</button>
				))}
			</div>

			{/* Find Rotation Staff */}
			{tab === 'find' && (
				<RotationStaffFinder
					locationId={selectedLocationId ?? null}
					homeCity={selectedLocation?.city ?? null}
					homeState={selectedLocation?.state ?? null}
					locations={
						profile?.locations?.map(l => ({
							id: l.id,
							locationName: l.locationName,
							address: l.address,
							city: l.city,
							state: l.state,
							active: l.active,
						})) ?? []
					}
				/>
			)}

			{/* Loading State */}
			{tab === 'team' && isLoading && (
				<div className="py-16 text-center">
					<Spinner size="lg" className="mx-auto mb-3 text-purple-600" />
					<p className="text-sm text-gray-500 dark:text-gray-400">Loading staff...</p>
				</div>
			)}

			{/* Error State */}
			{tab === 'team' && !isLoading && error && (
				<div className="py-16 text-center">
					<ExclamationTriangleIcon className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
					<h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
						Failed to load staff
					</h3>
					<p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{error}</p>
					{selectedLocationId && (
						<Button
							variant="primary"
							onClick={() => fetchStaffList(selectedLocationId)}
						>
							Try Again
						</Button>
					)}
				</div>
			)}

			{/* Content */}
			{tab === 'team' && !isLoading && !error && (
				<>
					{/* Stats Grid */}
					<StatsGrid staff={filteredStaff} />

					{/* Filter Bar */}
					<div className="settings-card mb-6">
						<div className="settings-card__body">
							<div className="staff-filters">
								<div className="staff-filters__selects">
									<select
										className="provider-form__input"
										value={roleFilter}
										onChange={e => setRoleFilter(e.target.value)}
									>
										<option value="">All Roles</option>
										{roles.map(r => (
											<option key={r.id} value={r.id}>
												{r.name}
											</option>
										))}
									</select>
									<select
										className="provider-form__input"
										value={statusFilter}
										onChange={e => setStatusFilter(e.target.value)}
									>
										<option value="">All Status</option>
										<option value="ACTIVE">Active</option>
										<option value="ON_LEAVE">On Leave</option>
										<option value="INACTIVE">Inactive</option>
										<option value="TERMINATED">Terminated</option>
									</select>
								</div>
								<div className="staff-filters__search">
									<MagnifyingGlassIcon className="staff-filters__search-icon" />
									<input
										type="text"
										className="provider-form__input staff-filters__search-input"
										placeholder="Search staff..."
										value={searchQuery}
										onChange={e => setSearchQuery(e.target.value)}
									/>
								</div>
							</div>
						</div>
					</div>

					{/* Staff Table (Desktop) */}
					<div className="staff-table-wrapper hidden md:block">
						<div className="settings-card">
							<div className="overflow-x-auto">
								<table className="staff-table">
									<thead>
										<tr>
											<th>Staff Member</th>
											<th>Status</th>
											<th>Grade</th>
											<th>Type</th>
											<th>Works At</th>
											<th>Personal Info</th>
											<th className="text-right">Actions</th>
										</tr>
									</thead>
									<tbody>
										{filteredStaff.length === 0 ? (
											<tr>
												<td
													colSpan={7}
													className="text-center py-10 text-gray-400"
												>
													No staff members found matching your filters.
												</td>
											</tr>
										) : (
											filteredStaff.map(staff => (
												<tr
													key={staff.id}
													className="staff-table__row"
													onClick={() => handleViewStaff(staff)}
												>
													<td>
														<div className="flex items-center gap-3">
															<div
																className="staff-table__avatar"
																style={{
																	background: getAvatarColor(
																		staff.id
																	),
																}}
															>
																{getInitials(
																	staff.firstName,
																	staff.lastName
																)}
															</div>
															<div>
																<div className="font-medium text-gray-900 dark:text-gray-100">
																	{staff.firstName}{' '}
																	{staff.lastName}
																</div>
																<div className="text-xs text-gray-500 dark:text-gray-400">
																	{staff.roleName} ·{' '}
																	{staff.staffGradeName}
																</div>
															</div>
														</div>
													</td>
													<td>{getStatusBadge(staff)}</td>
													<td>
														<span className="text-sm text-gray-700 dark:text-gray-300">
															{staff.staffGradeName}
														</span>
													</td>
													<td>
														<span className="text-sm text-gray-700 dark:text-gray-300">
															{staff.staffTypeName}
														</span>
													</td>
													<td>
														<WorksAtCell
															staff={staff}
															onOpen={() => setTravelStaff(staff)}
														/>
													</td>
													<td>
														<InfoRequestCell
															info={infoRequests.get(staff.id)}
															isSending={
																sendingInfoRequestId === staff.id
															}
															onSend={() =>
																handleSendInfoRequest(staff)
															}
														/>
													</td>
													<td className="text-right">
														<div className="flex items-center justify-end gap-1">
															<button
																type="button"
																className="p-2 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-400 transition-all cursor-pointer"
																onClick={e => {
																	e.stopPropagation();
																	handleViewStaff(staff);
																}}
																title="View details"
															>
																<EyeIcon className="w-4 h-4" />
															</button>
															<button
																type="button"
																className="p-2 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-400 transition-all cursor-pointer"
																onClick={e => {
																	e.stopPropagation();
																	handleEditStaff(staff);
																}}
																title="Edit"
															>
																<PencilSquareIcon className="w-4 h-4" />
															</button>
														</div>
													</td>
												</tr>
											))
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>

					{/* Staff Cards (Mobile) */}
					<div className="staff-cards md:hidden">
						{filteredStaff.length === 0 ? (
							<div className="settings-card">
								<div className="settings-card__body text-center py-10 text-gray-400">
									No staff members found matching your filters.
								</div>
							</div>
						) : (
							filteredStaff.map(staff => (
								<div
									key={staff.id}
									className="staff-card"
									onClick={() => handleViewStaff(staff)}
								>
									<div className="staff-card__header">
										<div
											className="staff-card__avatar"
											style={{ background: getAvatarColor(staff.id) }}
										>
											{getInitials(staff.firstName, staff.lastName)}
										</div>
										<div className="staff-card__info">
											<h4 className="staff-card__name">
												{staff.firstName} {staff.lastName}
											</h4>
											<div className="flex items-center gap-2">
												{getRoleBadge(staff)}
											</div>
										</div>
										{getStatusBadge(staff)}
									</div>
									<div className="staff-card__body">
										<div className="staff-card__field">
											<span className="staff-card__field-label">Grade</span>
											<span className="staff-card__field-value">
												{staff.staffGradeName}
											</span>
										</div>
										<div className="staff-card__field">
											<span className="staff-card__field-label">Type</span>
											<span className="staff-card__field-value">
												{staff.staffTypeName}
											</span>
										</div>
										<div className="staff-card__field">
											<span className="staff-card__field-label">Works At</span>
											<span className="staff-card__field-value">
												<WorksAtCell
													staff={staff}
													onOpen={() => setTravelStaff(staff)}
												/>
											</span>
										</div>
										<div className="staff-card__field staff-card__field--full">
											<span className="staff-card__field-label">
												Personal Info
											</span>
											<div className="staff-card__field-value">
												<InfoRequestCell
													info={infoRequests.get(staff.id)}
													isSending={sendingInfoRequestId === staff.id}
													onSend={() => handleSendInfoRequest(staff)}
												/>
											</div>
										</div>
									</div>
								</div>
							))
						)}
					</div>
				</>
			)}

			{/* Staff Detail Drawer */}
			<StaffDetailDrawer
				staff={selectedStaff}
				isOpen={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
				onEdit={handleEditStaff}
			/>

			{/* Engagements & Travel */}
			<EngagementsTravelModal
				isOpen={travelStaff !== null}
				onClose={() => setTravelStaff(null)}
				staffId={travelStaff?.id ?? null}
				staffName={travelStaff ? `${travelStaff.firstName} ${travelStaff.lastName}` : ''}
				locationId={selectedLocationId ?? null}
			/>

			{/* Add/Edit Modal */}
			<AddEditStaffModal
				key={editingStaff?.id ?? 'add'}
				isOpen={isModalOpen}
				onClose={() => {
					setIsModalOpen(false);
					setEditingStaff(null);
				}}
				staff={editingStaff}
				onSave={handleSaveStaff}
				isSaving={isSaving}
				allServiceTasks={allServiceTasks}
				isLoadingServiceTasks={isLoadingServiceTasks}
				roles={roles}
				locationId={selectedLocationId}
				locations={
					profile?.locations?.map(l => ({
						id: l.id,
						locationName: l.locationName,
						address: l.address,
						city: l.city,
						state: l.state,
						active: l.active,
					})) ?? []
				}
			/>
		</AppLayout>
	);
};

export default StaffManagement;
